import { Directive, ElementRef, Injectable, OnDestroy } from '@angular/core';
import * as angular from 'angular';
import { config, embedded, isMR, mrPathSegment } from 'angular/utils/config';

// handle links in AngularJS
angular.module('app').directive('ngHref', href);
angular.module('app').directive('href', href);
function href($location: ng.ILocationService) {
    return {
        restrict: 'A',
        link(scope, element, attrs) {
            // at this point `attrs.href` and `attrs.ngHref` might not be fully resolved, yet (race conditions).
            // that's why we check their values in the event handler.
            if (!embedded) return;

            scope.$watch(
                () => attrs.href,
                (
                    newValue: string | undefined,
                    oldValue: string | undefined
                ) => {
                    if (newValue && newValue.startsWith('#/')) {
                        // if a link targets angular, we'll rewrite it to be absolute to "/platform" and not
                        // relative to "/angular"
                        // the timeout is sadly needed whenever `newValue` contains query params
                        // somehow our `href` would not get updated otherwise :(
                        setTimeout(
                            () =>
                                element.attr(
                                    'href',
                                    config.appBaseUrl + '/platform/' + newValue
                                ),
                            1
                        );
                    }
                }
            );

            element.bind('click', (event: MouseEvent) => {
                // note: at this point in time `ng-href` will have set an `href` attribute, so we can
                // safely check just `href`
                handleClick(event, attrs.href, $location);
            });
        }
    };
}

// handle links in Angular
@Injectable()
@Directive({ selector: 'a[href]' })
export class HrefDirective implements OnDestroy {
    element: HTMLAnchorElement;

    constructor(private el: ElementRef) {
        this.element = this.el.nativeElement as HTMLAnchorElement;
        if (!embedded) return;

        this.element.addEventListener('click', this.onClick);
    }

    onClick = (event: MouseEvent) => {
        const { href } = this.element;
        handleClick(event, href, (window as any).__angularLocation);
    };

    ngOnDestroy() {
        if (!embedded) return;
        this.element.removeEventListener('click', this.onClick);
    }
}

// take care of properly updating our URLs, when Angular is embedded in React
function handleClick(
    event: MouseEvent,
    href: string,
    $location: ng.ILocationService
) {
    // note: in case `ng-href` was just an empty string, `href` can actually be `null`
    if (!href) return;

    const isReactUrl = href.startsWith(config.appBaseUrl);

    if (!isReactUrl) return;

    event.preventDefault();
    const url = new URL(href);
    // this will properly encode all searchParams
    const search = url.searchParams.toString();
    $location
        .search('') // reset any existing search first or the old search might be forwarded to the react page
        .path(
            '/react' +
                (isMR
                    ? url.pathname.replace(`/${mrPathSegment}`, '')
                    : url.pathname) +
                (search ? '?' + search : '')
        )
        .replace();
}

export function navigateToReact(url: string, $location: ng.ILocationService) {
    if (embedded) {
        $location.path(url).replace();
    } else {
        location.href = url;
    }
}
